// Espace employé — "Mes documents": self-service attestation PDF,
// delivered documents, in-flight requests. Deviation from the design
// prototype: there is no payslip depository in the product, so the
// fiche de paie grid becomes a request flow handled by the RH, and
// the attestation de travail (copie électronique) is a direct PDF
// download generated by the server.
import type { MeDocRequestDTO } from '../../api/hooks.js';
import { cgFmtLong, cgFmtRange, CG_MONTHS } from '../conges/common.js';
import { DOC_SERVICE, DOC_ST, ES_SERVICES, ES_TONE, EsTrack, type ServiceKey } from './common.js';
import { OPEN_DOC } from './EsRequestsList.js';

const monthsLbl = (months: string[]) => months.map((k) => CG_MONTHS[Number(k.slice(-2)) - 1]).join(', ');

export function EsDocsView({ docs, onRequest }: {
  docs: MeDocRequestDTO[];
  onRequest: (k: ServiceKey) => void;
}) {
  const delivered = docs.filter((d) => d.status === 'PRETE' || d.status === 'ENVOYEE');
  const inFlight = docs.filter((d) => OPEN_DOC.has(d.status));
  return (
    <div className="es-docsview">
      <div className="es-dv-head">
        <div>
          <div className="gm-eyebrow">Documents & attestations</div>
          <h1 className="gm-title sm">Mes documents</h1>
          <p className="es-dv-sub">
            L'attestation de travail se télécharge immédiatement. Les autres documents (ATS, attestation de congé,
            fiches de paie…) sont préparés par la RH — copie électronique par email ou original signé DG à retirer au siège.
          </p>
        </div>
      </div>

      <div className="es-dv-sect">
        <span className="es-dv-l">Disponible immédiatement</span>
      </div>
      <div className="es-selfcard">
        <span className="es-item-ic" style={{ background: 'var(--shfs)', color: 'var(--shf)' }}>▤</span>
        <div className="es-selfcard-m">
          <b>Attestation de travail — copie électronique</b>
          <i>Poste, ancienneté et entité, générée à la date du jour au format PDF.</i>
        </div>
        <a className="es-mini-btn" href="/api/me/attestation" target="_blank" rel="noreferrer">Télécharger le PDF</a>
        <button className="es-mini-btn ghost" onClick={() => onRequest('attestation')}>Original signé DG</button>
      </div>

      <div className="es-dv-sect">
        <span className="es-dv-l">Demandes en cours<em>{inFlight.length}</em></span>
        <button className="es-dv-act" onClick={() => onRequest('paie')}>Demander des fiches de paie →</button>
      </div>
      <div className="es-list">
        {inFlight.length === 0 && <div className="cg-empty">Aucune demande de document en cours.</div>}
        {inFlight.map((d) => {
          const svc = ES_SERVICES[DOC_SERVICE[d.type]];
          const [bg, fg] = ES_TONE[svc.tone];
          const [lbl, cls] = DOC_ST[d.status];
          return (
            <div key={d.id} className="es-item attente" style={{ cursor: 'default' }}>
              <span className="es-item-ic" style={{ background: bg, color: fg }}>{svc.ic}</span>
              <div className="es-item-m">
                <b>
                  {svc.lbl}
                  {d.months.length > 0 && ` · ${monthsLbl(d.months)}`}
                  {d.leaveStart && d.leaveEnd && ` · ${cgFmtRange(d.leaveStart, d.leaveEnd)}`}
                </b>
                <i>{d.forme === 'ORIGINAL' ? 'Original signé par le DG' : 'Copie électronique'} · demandé le {cgFmtLong(d.createdAt.slice(0, 10))}</i>
                <div className="es-item-tl">
                  <EsTrack status={d.status} forme={d.forme} />
                </div>
              </div>
              <span className={'es-chip ' + cls}>{lbl}</span>
            </div>
          );
        })}
      </div>

      <div className="es-dv-sect">
        <span className="es-dv-l">Documents délivrés<em>{delivered.length}</em></span>
        <button className="es-dv-act" onClick={() => onRequest('ats')}>Nouvelle attestation →</button>
      </div>
      <div className="es-list">
        {delivered.length === 0 && <div className="cg-empty">Aucun document délivré pour l'instant.</div>}
        {delivered.map((d) => {
          const svc = ES_SERVICES[DOC_SERVICE[d.type]];
          const [bg, fg] = ES_TONE[svc.tone];
          const [lbl, cls] = DOC_ST[d.status];
          return (
            <div key={d.id} className="es-item" style={{ cursor: 'default' }}>
              <span className="es-item-ic" style={{ background: bg, color: fg }}>{svc.ic}</span>
              <div className="es-item-m">
                <b>
                  {svc.lbl}
                  {d.months.length > 0 && ` · ${monthsLbl(d.months)}`}
                  {d.leaveStart && d.leaveEnd && ` · ${cgFmtRange(d.leaveStart, d.leaveEnd)}`}
                </b>
                <i>
                  {d.forme === 'ORIGINAL' ? 'Original signé par le DG' : 'Copie électronique signée'}
                  {d.dest ? ` · ${d.dest}` : ''} · délivré le {cgFmtLong((d.doneAt ?? d.createdAt).slice(0, 10))}
                </i>
              </div>
              {d.status === 'PRETE' && <span className="es-pickup">À retirer · RH siège</span>}
              <span className={'es-chip ' + cls}>{lbl}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
